import React, { useState, useEffect, useRef } from 'react';
import { accueilAPI, calendrierAPI, departementsAPI, authAPI } from '../services/api';
import AppSidebar, { getModuleTopbarColor } from './AppSidebar';
import './AccueilDashboard.css';
// Lot D.1 : le bandeau du super-administrateur (l'Accueil n'est pas dans AppLayout).
import { BandeauConservation } from './DocumentsConservation';

// ----------------------------------------------------------------
// Helper : couleur de vignette selon niveau d'urgence
// ----------------------------------------------------------------
const NIVEAUX_CONFIG = {
    urgent: { cls: 'vignette-urgent', label: 'Urgent' },
    warn:   { cls: 'vignette-warn',   label: 'Bientôt' },
    avenir: { cls: 'vignette-avenir', label: 'Planifié' },
    done:   { cls: 'vignette-done',   label: 'Terminé' },
    neutre: { cls: 'vignette-neutre', label: '' },
};

function Vignette({ niveau, texte }) {
    const cfg = NIVEAUX_CONFIG[niveau] || NIVEAUX_CONFIG.neutre;
    return (
        <div className={`vignette ${cfg.cls}`}>
            <span className="vignette-dot" />
            <span className="vignette-text">{texte}</span>
        </div>
    );
}

function VignetteVide({ icone, label }) {
    return (
        <div className="vignette vignette-neutre">
            <span>{icone}</span>
            <span className="vignette-text">Aucun {label} planifié</span>
        </div>
    );
}

// ----------------------------------------------------------------
// Ordre par défaut des stat-cards
// ----------------------------------------------------------------
const DEFAULT_CARD_ORDER = ['planning', 'etudiants', 'soutenances', 'voeux', 'stages'];

// ----------------------------------------------------------------
// Composant principal
// ----------------------------------------------------------------
function AccueilDashboard({ user, config, onNavigate, onLogout }) {
    const [data, setData]             = useState(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [annees, setAnnees]         = useState([]);
    const [anneeSelectId, setAnneeSelectId] = useState(null);
    const [planningStats, setPlanningStats] = useState(null);
    const [cardOrder, setCardOrder]   = useState(
        () => user?.preferences?.accueilCardOrder || DEFAULT_CARD_ORDER
    );
    const [dragOverCard, setDragOverCard] = useState(null);
    const dragCard = useRef(null);

    const deptId  = config?.departement?.id;
    const anneeId = anneeSelectId || config?.anneeScolaireId;
    const deptNom = config?.departement?.nom || config?.departement?.titre_planning || 'Département';
    const moduleColor = getModuleTopbarColor('accueil');

    // Charger toutes les années scolaires disponibles
    useEffect(() => {
        calendrierAPI.getAll()
            .then(res => {
                const liste = res.data?.annees || [];
                setAnnees(liste);
                if (!anneeSelectId && liste.length > 0) {
                    const active = liste.find(a => a.active);
                    if (active) setAnneeSelectId(active.id);
                }
            })
            .catch(() => {});
    }, []);

    // Charger les données du dashboard
    useEffect(() => {
        if (!deptId || !anneeId) return;
        setLoading(true);
        setError(null);
        accueilAPI.getData(deptId, anneeId)
            .then(res => { setData(res.data); setLoading(false); })
            .catch(err => {
                console.error(err);
                setError('Impossible de charger le tableau de bord.');
                setLoading(false);
            });
    }, [deptId, anneeId]);

    // Drag & drop stat-cards
    const handleCardDragStart = (key) => { dragCard.current = key; };
    const handleCardDragOver  = (e, key) => { e.preventDefault(); setDragOverCard(key); };
    const handleCardDragLeave = () => setDragOverCard(null);
    const handleCardDragEnd   = () => { dragCard.current = null; setDragOverCard(null); };
    const handleCardDrop      = (key) => {
        if (!dragCard.current || dragCard.current === key) { setDragOverCard(null); return; }
        const newOrder = [...cardOrder];
        const fromIdx  = newOrder.indexOf(dragCard.current);
        const toIdx    = newOrder.indexOf(key);
        newOrder.splice(fromIdx, 1);
        newOrder.splice(toIdx, 0, dragCard.current);
        setCardOrder(newOrder);
        setDragOverCard(null);
        const newPrefs = { ...(user?.preferences || {}), accueilCardOrder: newOrder };
        authAPI.savePreferences(newPrefs).catch(() => {});
        const updatedUser = { ...user, preferences: newPrefs };
        localStorage.setItem('user', JSON.stringify(updatedUser));
    };

    // Charger les stats Planning
    useEffect(() => {
        if (!deptId) return;
        departementsAPI.getAccueilStats(deptId)
            .then(res => setPlanningStats(res.data))
            .catch(() => setPlanningStats({ nbEvenements: 0 }));
    }, [deptId]);

    // Grouper la structure par niveau
    const niveaux = data
        ? data.structure.reduce((acc, row) => {
            let n = acc.find(x => x.niveau_id === row.niveau_id);
            if (!n) {
                n = {
                    niveau_id:   row.niveau_id,
                    niveau_code: row.niveau_code,
                    niveau_nom:  row.niveau_nom,
                    niveau_ordre: row.niveau_ordre,
                    sous_niveaux: []
                };
                acc.push(n);
            }
            n.sous_niveaux.push(row);
            return acc;
        }, [])
        : [];

    // Total étudiants
    const totalEtudiants = data
        ? data.structure.reduce((s, r) => s + parseInt(r.nb_etudiants || 0), 0)
        : 0;

    // Couleur badge niveau
    const COULEURS_NIVEAU = ['#1e40af', '#0f766e', '#7c3aed', '#b45309'];

    return (
        <div className="accueil-layout">

            {/* ---- SIDEBAR COMMUNE ---- */}
            <AppSidebar
                activeKey="accueil"
                user={user}
                config={config}
                onNavigate={onNavigate}
                onLogout={onLogout}
                annees={annees}
                anneeId={anneeId}
                onAnneeChange={setAnneeSelectId}
            />

            {/* ---- CONTENU ---- */}
            <div className="accueil-main">
                <BandeauConservation user={user} />

                {/* Barre de titre — couleur charte graphique */}
                <div
                    className="accueil-topbar"
                    style={{ background: `linear-gradient(135deg, ${moduleColor} 0%, ${moduleColor}cc 100%)` }}
                >
                    <div>
                        <div className="accueil-topbar-title">
                            <i className="fa-solid fa-house" style={{ marginRight: 8 }} />
                            Tableau de bord
                        </div>
                        <div className="accueil-topbar-sub">{deptNom} — Année {config?.anneeScolaire?.libelle || ''}</div>
                    </div>
                    <div className="accueil-topbar-right">
                        <span>👤 {user?.prenom} {user?.nom}</span>
                        <span style={{ opacity: 0.5 }}>|</span>
                        <span style={{ opacity: 0.75, fontSize: '0.8rem' }}>{user?.role_nom}</span>
                    </div>
                </div>

                {/* Corps */}
                <div className="accueil-body">

                    {loading && (
                        <div className="accueil-loading">
                            <div className="accueil-spinner" />
                            <p>Chargement du tableau de bord...</p>
                        </div>
                    )}

                    {error && (
                        <div style={{ padding: 20, color: 'var(--danger)', background: '#fef2f2', borderRadius: 8, marginBottom: 20 }}>
                            ⚠️ {error}
                        </div>
                    )}

                    {!loading && data && (
                        <>
                            {/* Stats résumé — draggable */}
                            <div className="accueil-stats-row">
                                {cardOrder.map(key => {
                                    const isDragOver = dragOverCard === key;
                                    const dragProps = {
                                        draggable: true,
                                        onDragStart: () => handleCardDragStart(key),
                                        onDragOver:  (e) => handleCardDragOver(e, key),
                                        onDragLeave: handleCardDragLeave,
                                        onDrop:      () => handleCardDrop(key),
                                        onDragEnd:   handleCardDragEnd,
                                    };

                                    if (key === 'planning') return (
                                        <div
                                            key="planning"
                                            className={`stat-card stat-card-planning ${isDragOver ? 'stat-card-drag-over' : ''}`}
                                            onClick={() => onNavigate('planning')}
                                            title="Ouvrir le Planning Annuel"
                                            {...dragProps}
                                        >
                                            <span className="stat-card-drag-handle">⠿</span>
                                            <span className="stat-card-icon">📅</span>
                                            <div className="stat-card-planning-indicators">
                                                <div className="stat-card-planning-indicator">
                                                    <div className="stat-card-value">🚧</div>
                                                    <div className="stat-card-label">Formations prévues</div>
                                                </div>
                                                <div className="stat-card-planning-separator" />
                                                <div className="stat-card-planning-indicator">
                                                    <div className="stat-card-value" style={{ color: '#2563eb' }}>
                                                        {planningStats ? planningStats.nbEvenements : '—'}
                                                    </div>
                                                    <div className="stat-card-label">Événements placés</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                    if (key === 'etudiants') return (
                                        <div key="etudiants" className={`stat-card ${isDragOver ? 'stat-card-drag-over' : ''}`} {...dragProps}>
                                            <span className="stat-card-drag-handle">⠿</span>
                                            <span className="stat-card-icon">🎓</span>
                                            <div>
                                                <div className="stat-card-value">{totalEtudiants}</div>
                                                <div className="stat-card-label">Étudiants actifs</div>
                                            </div>
                                        </div>
                                    );
                                    if (key === 'soutenances') return (
                                        <div key="soutenances" className={`stat-card ${isDragOver ? 'stat-card-drag-over' : ''}`} {...dragProps}>
                                            <span className="stat-card-drag-handle">⠿</span>
                                            <span className="stat-card-icon">🎤</span>
                                            <div>
                                                <div className="stat-card-value">{data.vignettes.soutenances.length}</div>
                                                <div className="stat-card-label">Soutenances à planifier</div>
                                            </div>
                                        </div>
                                    );
                                    if (key === 'voeux') return (
                                        <div key="voeux" className={`stat-card ${isDragOver ? 'stat-card-drag-over' : ''}`} {...dragProps}>
                                            <span className="stat-card-drag-handle">⠿</span>
                                            <span className="stat-card-icon">⭐</span>
                                            <div>
                                                <div className="stat-card-value">{data.vignettes.voeux.length}</div>
                                                <div className="stat-card-label">Campagnes vœux ouvertes</div>
                                            </div>
                                        </div>
                                    );
                                    if (key === 'stages') return (
                                        <div key="stages" className={`stat-card ${isDragOver ? 'stat-card-drag-over' : ''}`} {...dragProps}>
                                            <span className="stat-card-drag-handle">⠿</span>
                                            <span className="stat-card-icon">🏢</span>
                                            <div>
                                                <div className="stat-card-value">
                                                    {/* Lot 3.7c : fiches + conventions en attente, toutes campagnes. */}
                                                    {data.vignettes.stage && !data.vignettes.stage.erreur ? data.vignettes.stage.nb_total : '—'}
                                                </div>
                                                <div className="stat-card-label">Stages en attente</div>
                                            </div>
                                        </div>
                                    );
                                    return null;
                                })}
                            </div>

                            {/* Grille niveaux — 🚧 non opérationnel */}
                            <div style={{ position: 'relative' }}>
                                <div className="accueil-section-title section-dev-overlay-title">
                                    Structure de la formation
                                    <span className="section-dev-badge">🚧</span>
                                </div>
                            <div className="niveaux-grid niveaux-grid-dev">
                                {niveaux.map((n, idx) => (
                                    <div key={n.niveau_id} className="niveau-card">
                                        <div className="niveau-card-header">
                                            <span
                                                className="niveau-badge"
                                                style={{ background: COULEURS_NIVEAU[idx % COULEURS_NIVEAU.length] }}
                                            >
                                                {n.niveau_code}
                                            </span>
                                            <span className="niveau-card-nom">{n.niveau_nom}</span>
                                        </div>
                                        <div className="niveau-card-body">
                                            {n.sous_niveaux.map(sn => (
                                                <div key={sn.sous_niveau_id} className="sous-niveau-cell">
                                                    <div className="sous-niveau-header">
                                                        <span
                                                            className="sous-niveau-dot"
                                                            style={{ background: sn.couleur || '#64748b' }}
                                                        />
                                                        <span className="sous-niveau-code">{sn.sous_niveau_code}</span>
                                                        <span className="sous-niveau-effectif">
                                                            {sn.nb_etudiants} étudiant{sn.nb_etudiants !== 1 ? 's' : ''}
                                                        </span>
                                                    </div>
                                                    <div className="vignettes-zone">
                                                        <Vignette
                                                            niveau="neutre"
                                                            texte="Aucun événement imminent"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {niveaux.length === 0 && (
                                    <div style={{ color: 'var(--secondary)', fontStyle: 'italic', padding: '20px 0' }}>
                                        Aucune formation configurée pour ce département.
                                    </div>
                                )}
                            </div>
                            </div>{/* fin wrapper section formation */}

                            {/* Vignettes modules globales */}
                            <div className="accueil-vignettes-section">
                                <div className="accueil-section-title" style={{ marginTop: 12 }}>
                                    Suivi des modules
                                </div>
                                <div className="vignettes-globales-grid">

                                    {/* Soutenances */}
                                    <div className="vignette-module-card vignette-module-dev">
                                        <div className="vignette-dev-overlay"><span>🚧</span></div>
                                        <div className="vignette-module-title">🎤 Soutenances</div>
                                        {data.vignettes.soutenances.length === 0
                                            ? <VignetteVide icone="🎤" label="soutenance" />
                                            : data.vignettes.soutenances.map(s => (
                                                <Vignette
                                                    key={s.id}
                                                    niveau={s.niveau}
                                                    texte={`${s.titre} — J${s.jours_restants >= 0 ? '-' : '+'}${Math.abs(s.jours_restants)}j`}
                                                />
                                            ))
                                        }
                                    </div>

                                    {/* Vœux */}
                                    <div className="vignette-module-card vignette-module-dev">
                                        <div className="vignette-dev-overlay"><span>🚧</span></div>
                                        <div className="vignette-module-title">⭐ Vœux</div>
                                        {data.vignettes.voeux.length === 0
                                            ? <VignetteVide icone="⭐" label="campagne" />
                                            : data.vignettes.voeux.map(v => (
                                                <Vignette
                                                    key={v.id}
                                                    niveau={v.niveau}
                                                    texte={`${v.titre} — fermeture J${v.jours_restants >= 0 ? '-' : '+'}${Math.abs(v.jours_restants)}j`}
                                                />
                                            ))
                                        }
                                    </div>

                                    {/* Stages */}
                                    <div className="vignette-module-card vignette-module-dev">
                                        <div className="vignette-dev-overlay"><span>🚧</span></div>
                                        <div className="vignette-module-title">🏢 Stages / Alternance</div>
                                        {/* Lot 3.7c : une ligne par campagne, d'après la spec — orange s'il reste
                                            des fiches ou des conventions en attente, vert sinon. ⚠️ Une lecture
                                            impossible se DIT (E23 : ce bloc a été muet pendant des mois). */}
                                        {!data.vignettes.stage
                                            ? <VignetteVide icone="🏢" label="stage" />
                                            : data.vignettes.stage.erreur
                                                ? <Vignette niveau="urgent" texte={data.vignettes.stage.erreur} />
                                                : data.vignettes.stage.campagnes.map((c) => (
                                                    <Vignette
                                                        key={c.id}
                                                        niveau={c.niveau}
                                                        texte={`🏢 ${c.formation_nom} ${c.semestre} — 📋 ${c.nb_fiches} fiche${c.nb_fiches > 1 ? 's' : ''} en attente · 📄 ${c.nb_conventions} convention${c.nb_conventions > 1 ? 's' : ''} en attente`}
                                                    />
                                                ))
                                        }
                                    </div>

                                    {/* EDT Builder */}
                                    <div
                                        className="vignette-module-card vignette-module-card-link"
                                        onClick={() => onNavigate('edt')}
                                        title="Ouvrir EDT Builder"
                                        style={{ cursor: 'pointer', borderColor: getModuleTopbarColor('edt') }}
                                    >
                                        <div className="vignette-module-title">
                                            <i className="fa-solid fa-table-cells" style={{ color: getModuleTopbarColor('edt'), marginRight: 6 }} />
                                            EDT Builder
                                        </div>
                                        <div style={{ fontSize: '0.78rem', color: getModuleTopbarColor('edt'), marginTop: 6, fontWeight: 600 }}>
                                            Planification emplois du temps → Pronote
                                        </div>
                                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 4 }}>
                                            Cliquer pour ouvrir le module
                                        </div>
                                    </div>

                                    {/* Poursuite */}
                                    <div className="vignette-module-card vignette-module-dev">
                                        <div className="vignette-dev-overlay"><span>🚧</span></div>
                                        <div className="vignette-module-title">📋 Poursuite d'études</div>
                                        {!data.vignettes.poursuite
                                            ? <VignetteVide icone="📋" label="jury" />
                                            : (
                                                <>
                                                    <Vignette
                                                        niveau={data.vignettes.poursuite.niveau}
                                                        texte={`Jury dans J-${data.vignettes.poursuite.jours_restants}j`}
                                                    />
                                                    <Vignette
                                                        niveau={data.vignettes.poursuite.nb_validees === data.vignettes.poursuite.nb_fiches && data.vignettes.poursuite.nb_fiches > 0 ? 'done' : 'warn'}
                                                        texte={`${data.vignettes.poursuite.nb_validees}/${data.vignettes.poursuite.nb_fiches} fiches validées`}
                                                    />
                                                </>
                                            )
                                        }
                                    </div>

                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AccueilDashboard;
